/**
 * F-016 Edit Proposal — the approval-gated staged change (GLOSSARY: Companion
 * & Source Relevance; data-model §1.2/§2.1). This module is the behavioural
 * core shared by the API's use cases: the persisted-status model, the derived
 * stale/expired projections (I-CC-03), and the single set of decidability
 * predicates the statechart guards and the projection BOTH consume — so the
 * machine and the read model can never disagree.
 *
 * Rows are deliberately NOT modelled here (they are ContentItem shapes owned
 * by the contracts SSOT); the domain reasons over the proposal's lifecycle
 * facts only. Time is an input (`nowMs`) — no clocks in the domain.
 */

/** Persisted statuses — the ONLY values a store may hold (I-CC-03). */
export type PersistedProposalStatus = 'pending' | 'applied' | 'rejected';

/** Presentation statuses — persisted plus the derived projections of pending. */
export type DerivedProposalStatus = PersistedProposalStatus | 'stale' | 'expired';

export type StaleReason =
  | 'stale-version'
  | 'test-case-not-editable'
  | 'trial-not-editable'
  | 'expired'
  | 'already-decided';

/** The lifecycle-relevant facts of a stored proposal. */
export type ProposalSnapshot = {
  readonly status: PersistedProposalStatus;
  readonly baseVersion: number;
  readonly createdAtMs: number;
};

/** The current state of the proposal's target, as loaded by the caller. */
export type ProposalTarget = {
  readonly testCaseExists: boolean;
  readonly testCaseExcluded: boolean;
  readonly testCaseVersion: number | undefined;
  readonly trialEditable: boolean;
};

/** 24 h validity window (spec Assumptions; FR-022). */
export const PROPOSAL_VALIDITY_MS = 24 * 60 * 60 * 1000;

export const isExpired = (proposal: ProposalSnapshot, nowMs: number): boolean =>
  nowMs > proposal.createdAtMs + PROPOSAL_VALIDITY_MS;

/** FR-020's "excluded or removed" — one predicate, one reason. */
export const isTestCaseEditable = (target: ProposalTarget): boolean =>
  target.testCaseExists && !target.testCaseExcluded;

export const baseVersionMatches = (proposal: ProposalSnapshot, target: ProposalTarget): boolean =>
  target.testCaseVersion !== undefined && target.testCaseVersion === proposal.baseVersion;

/**
 * Why a decision on this proposal would be refused right now — `undefined`
 * means it is decidable. Reason precedence mirrors the user's mental model:
 * a decided proposal is "already decided" regardless of drift; then expiry;
 * then target editability; then version drift.
 */
export const decisionBlockReason = (
  proposal: ProposalSnapshot,
  target: ProposalTarget,
  nowMs: number,
): StaleReason | undefined => {
  if (proposal.status !== 'pending') return 'already-decided';
  if (isExpired(proposal, nowMs)) return 'expired';
  if (!target.trialEditable) return 'trial-not-editable';
  if (!isTestCaseEditable(target)) return 'test-case-not-editable';
  if (!baseVersionMatches(proposal, target)) return 'stale-version';
  return undefined;
};

/**
 * The derived presentation status (data-model §1.2): stale/expired are
 * projections of `pending`, never persisted. Rejection/expiry precedence is
 * the same predicate set the apply guard uses (`decisionBlockReason`).
 */
export const deriveProposalStatus = (
  proposal: ProposalSnapshot,
  target: ProposalTarget,
  nowMs: number,
): { readonly status: DerivedProposalStatus; readonly staleReason?: StaleReason } => {
  if (proposal.status !== 'pending') return { status: proposal.status };
  const reason = decisionBlockReason(proposal, target, nowMs);
  if (reason === undefined) return { status: 'pending' };
  if (reason === 'expired') return { status: 'expired', staleReason: 'expired' };
  return { status: 'stale', staleReason: reason };
};
